import React, { Component } from 'react';
import ReactDateTime from 'react-datetime';
import { string, func } from 'prop-types';
import moment, { isMoment } from 'moment';
import 'react-datetime/css/react-datetime.css';
import styles from './dateTimePicker.module.css';

const toMoment = (value, dateFormat) => {
  if (value) {
    return moment(value, dateFormat, true);
  }
  return undefined;
};

/* Updates react-datetime component's time picker separator and style based on locale.

 When locale is fi, dot used to separate hours and minutes.
 In other locales (sv, en) semicolon is used. Unfortunately we have to hack
 CSS style a bit to get the separator line up nicely with hours and minutes in the picker
 */
const updateTimePickerStyleAndSeparator = (locale, separatorNodes) => {
  let separator;
  let style;

  if (locale === 'fi') {
    separator = '.';
    style = 'line-height: 108px;';
  } else {
    separator = ':';
    style = 'line-height: 105px;';
  }

  separatorNodes.forEach((separatorNode) => {
    separatorNode.textContent = separator;
    separatorNode.setAttribute('style', style);
  });
};

class DateTimePicker extends Component {
  static initializeDateOrTime(defaultValue, dateOutputFormat) {
    return defaultValue === '' ? null : toMoment(defaultValue, dateOutputFormat);
  }

  constructor(props) {
    super(props);
    const { defaultValue, dateOutputFormat } = props;
    this.onDateChange = this.onDateChange.bind(this);
    this.onTimeChange = this.onTimeChange.bind(this);
    this.onDateTimeChange = this.onDateTimeChange.bind(this);
    this.validateDate = this.validateDate.bind(this);
    this.clickCalendar = this.clickCalendar.bind(this);
    this.clickClock = this.clickClock.bind(this);
    this.calendarOpen = false;
    this.calendarWasOpen = false;
    this.clockOpen = false;
    this.clockWasOpen = false;
    this.timePicker = React.createRef();
    const initialValue = DateTimePicker.initializeDateOrTime(defaultValue, dateOutputFormat);
    this.state = {
      minDate: moment().startOf('day'),
      date: initialValue,
      time: initialValue
    };
  }

  componentDidUpdate() {
    const { locale } = this.props;
    updateTimePickerStyleAndSeparator(locale, this.timePicker.querySelectorAll('.rdtCounterSeparator'));
  }

  onDateTimeChange() {
    const {
      date, time, invalidDate, invalidTime
    } = this.state;

    if (!invalidTime && !invalidDate
      && date && time && isMoment(date) && isMoment(time)) {
      const finalDateTime = date.hours(time.hours()).minutes(time.minutes());
      if (finalDateTime.isValid()) {
        this.props.onChange(finalDateTime.format(this.props.dateOutputFormat));
        return;
      }
    }
    this.props.onChange('invalid');
  }

  onDateChange(newDate) {
    const invalidDate = !moment(newDate, 'L', true).isValid();
    this.setState({ invalidDate, date: newDate }, () => this.onDateTimeChange());
  }

  onTimeChange(newTime) {
    const invalidTime = !moment(newTime, 'LT', true).isValid();
    this.setState({ invalidTime, time: newTime }, () => this.onDateTimeChange());
  }

  validateDate(date) {
    const { minDate } = this.state;

    return !(minDate && minDate.isAfter(date));
  }

  clickCalendar() {
    if (!(this.calendarOpen || this.calendarWasOpen)) {
      document.getElementById(`${this.props.fieldName}Calendar`).focus();
    }
  }

  clickClock() {
    if (!(this.clockOpen || this.clockWasOpen)) {
      document.getElementById(`${this.props.fieldName}Clock`).focus();
    }
  }

  render() {
    const {
      defaultValue, dateOutputFormat, fieldName, locale
    } = this.props;
    const timePickerClass = `${styles.datePickerItemContainer} ${this.state.invalidTime ? styles.invalid : ''}`;
    const datePickerClass = `${styles.datePickerItemContainer} ${this.state.invalidDate ? styles.invalid : ''}`;

    return (
      <div className={styles.datePickerContainer}>
        <div className={datePickerClass}>
          <ReactDateTime
            className={styles.datePicker}
            timeFormat={false}
            inputProps={{ id: `${fieldName}Calendar`, 'data-cy': 'date-picker-date-input' }}
            locale={locale}
            defaultValue={toMoment(defaultValue, dateOutputFormat)}
            onChange={this.onDateChange}
            isValidDate={this.validateDate}
            onBlur={() => { this.calendarOpen = false; }}
            onFocus={() => { this.calendarOpen = true; }}
          />
          <button
            type="button"
            className={`button icon--date ${styles.icon}`}
            onMouseDown={() => { this.calendarWasOpen = this.calendarOpen; }}
            onClick={this.clickCalendar}
            data-cy="date-picker-calendar-button"
          />
        </div>
        <div className={timePickerClass} ref={(el) => { this.timePicker = el; }}>
          <ReactDateTime
            className={styles.datePicker}
            dateFormat={false}
            inputProps={{ id: `${fieldName}Clock`, 'data-cy': 'date-picker-time-input' }}
            locale={locale}
            defaultValue={toMoment(defaultValue, dateOutputFormat)}
            onChange={this.onTimeChange}
            onBlur={() => { this.clockOpen = false; }}
            onFocus={() => { this.clockOpen = true; }}
          />
          <button
            type="button"
            className={`button icon--time ${styles.icon}`}
            onMouseDown={() => { this.clockWasOpen = this.clockOpen; }}
            onClick={this.clickClock}
          />
        </div>
      </div>
    );
  }
}

DateTimePicker.defaultProps = {
  defaultValue: '',
  fieldName: 'DateTimePicker'
};

DateTimePicker.propTypes = {
  defaultValue: string,
  onChange: func.isRequired,
  dateOutputFormat: string.isRequired,
  locale: string.isRequired,
  fieldName: string
};

export default DateTimePicker;
